import { useContext, useState, ReactNode } from "react";

// @mui material components
import Card from "@mui/material/Card";
import Divider from "@mui/material/Divider";
import Icon from "@mui/material/Icon";
import Tooltip from "@mui/material/Tooltip";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";

import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDButton from "components/MDButton";

import { LanguageContext } from "context";

interface Props {
  image: string;
  title: string;
  description: string;
  action?: ReactNode | boolean;
  isThumbUp?: boolean;
  isThumbDown?: boolean;
  onThumbUp?: () => void;
  onThumbDown?: () => void;
  disabledThumb?: boolean;
  [key: string]: any;
}

function StoryCard({
  image,
  title,
  description,
  action,
  isThumbUp,
  isThumbDown,
  onThumbUp,
  onThumbDown,
  disabledThumb,
}: Props): JSX.Element {
  const { t } = useContext(LanguageContext) as any;

  const [open, setOpen] = useState(false);

  const handleOpen = () => setOpen(true);
  const handleClose = () => setOpen(false);

  return (
    <>
      <Card
        sx={{
          "&:hover .card-header": {
            transform: action && "translate3d(0, -50px, 0)",
          },
          width: "100%",
          height: "100%",
          minHeight: "420px",
          display: "flex",
          flexDirection: "column",
          justifyContent: "space-between",
        }}
      >
        <MDBox
          position="relative"
          borderRadius="lg"
          mt={-3}
          mx={2}
          className="card-header"
          sx={{ 
            height: "310px",
            overflow: "hidden",
            transition: "transform 300ms cubic-bezier(0.34, 1.61, 0.7, 1)"
          }}
        >
          <MDBox
            component="img"
            src={image}
            alt={title}
            borderRadius="lg"
            shadow="md"
            width="100%"
            height="100%"
            position="relative"
            zIndex={1}
            sx={{
              objectFit: "cover",
              objectPosition: "center",
            }}
          />
          <MDBox
            borderRadius="lg"
            shadow="md"
            width="100%"
            height="100%"
            position="absolute"
            left={0}
            top="0"
            sx={{
              backgroundImage: `url(${image})`,
              transform: "scale(0.94)",
              filter: "blur(12px)",
              backgroundSize: "cover",
            }}
          />
        </MDBox>
        <MDBox textAlign="center" pt={3} px={3}>
          <MDBox
            display="flex"
            justifyContent="center"
            alignItems="center"
            mt={action ? -8 : -4.25}
          >
            {action}
          </MDBox>
          <MDTypography variant="h6" fontWeight="regular" sx={{ mt: 4 }}>
            {title}
          </MDTypography>
        </MDBox>
        <Divider />
        <MDBox
          display="flex"
          justifyContent="space-between"
          alignItems="center"
          pt={0.5}
          pb={3}
          px={3}
          lineHeight={1}
        >
          <MDBox display="flex" alignItems="center" gap={1}>
            <Tooltip title={t("StoryCard.good")}>
              <Icon
                color={isThumbUp ? "info" : "inherit"}
                sx={{
                  cursor: disabledThumb ? "not-allowed" : "pointer",
                  opacity: disabledThumb ? 0.5 : 1,
                }}
                onClick={disabledThumb ? undefined : onThumbUp}
              >
                thumb_up
              </Icon>
            </Tooltip>
            <Tooltip title={t("StoryCard.bad")}>
              <Icon
                color={isThumbDown ? "error" : "inherit"}
                sx={{
                  cursor: disabledThumb ? "not-allowed" : "pointer",
                  opacity: disabledThumb ? 0.5 : 1,
                }}
                onClick={disabledThumb ? undefined : onThumbDown}
              >
                thumb_down
              </Icon>
            </Tooltip>
          </MDBox>
          
          <MDButton
            variant="gradient"
            color="info"
            size="small"
            onClick={handleOpen}
            sx={{ padding: "4px 16px" }}
          >
            {t("StoryCard.detail") || "詳細"}
          </MDButton>
        </MDBox>
      </Card>

      <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
        <DialogTitle>{t("StoryCard.detail")}</DialogTitle>
        <DialogContent dividers>
          <MDTypography variant="body2" color="text">
            {description}
          </MDTypography>
        </DialogContent>
        <DialogActions>
          <MDButton variant="gradient" color="info" size="small" onClick={handleClose}>
            {t("StoryCard.close") || "閉じる"}
          </MDButton>
        </DialogActions>
      </Dialog>
    </>
  );
}

StoryCard.defaultProps = {
  action: false,
};

export default StoryCard;